import React from 'react';
import { Formik, Form } from 'formik';
import { useSelector } from 'react-redux';

import BeneficialOwnerRecord from '../../../records/beneficial_owner';
import CTAButton from '../../shared/CTAButton';
import ColumnInput from '../../shared/ColumnInput';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import { emailRegex, states } from '../../../lib/constants/SiteVariables';

export default function BeneficialOwnerCreate({ toggleAddOwner, addOwner }) {
  const isLoading = useSelector((state) => state.sessions.get('loading'));

  const saveNewOwners = (values) => {
    addOwner(new BeneficialOwnerRecord(values));
  };

  return (
    <div>
      <Formik
        initialValues={new BeneficialOwnerRecord().toJS()}
        validate={async (values) => {
          const errors = {};
          const addNested = (field, msg) =>
            (errors['address'] = { ...errors['address'], [field]: msg });

          if (!values.address.address1) addNested('address1', '* Required');
          if (!values.address.city) addNested('city', '* Required');
          if (!values.address.state) addNested('state', '* Required');
          if (!values.address.zip) {
            addNested('zip', '* Required');
          } else if (values.address.zip.trim().length < 5) {
            addNested('zip', '* Must be 5 characters in length');
          }

          if (!values.first_name) errors.first_name = '* Required';
          if (!values.last_name) errors.last_name = '* Required';
          if (!values.dob) errors.dob = '* Required';
          if (!values.ssn) {
            errors.ssn = '* Required';
          } else if (values.ssn.replace(/[^\d]/g, '').length < 9) {
            errors.ssn = '* Must be 9 characters in length';
          }
          if (!values.email) errors.email = '* Required';
          if (!emailRegex.test(values.email))
            errors.email = 'Please provide a valid email address';

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          saveNewOwners(values);
        }}
      >
        {({ isSubmitting, errors, touched, values }) => (
          <Form role="form">
            <ColumnInput
              error={errors.first_name}
              touched={touched.first_name || touched.last_name}
            >
              <StickyInput
                className="my-4"
                id="first_name"
                placeholder="First Name"
                values={values}
              />

              <StickyInput
                className="my-4"
                error={errors.last_name}
                id="last_name"
                placeholder="Last Name"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.dob}
              touched={touched.dob || touched.ssn}
              className="flex justify-between"
            >
              <StickyInput
                id="dob"
                mask="99/99/9999"
                placeholder="DOB (mm/dd/yyyy)"
                values={values}
              />

              <StickyInput
                error={errors.ssn}
                id="ssn"
                mask="999-99-9999"
                placeholder="SSN"
                values={values}
              />
            </ColumnInput>

            <ColumnInput error={errors.email} touched={touched.email}>
              <StickyInput
                id="email"
                placeholder="Email Address"
                type="email"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.address && errors.address.address1}
              touched={touched.address && touched.address.address1}
            >
              <StickyInput
                id="address.address1"
                placeholder="Street Address"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.address && errors.address.city}
              touched={
                touched.address &&
                (touched.address.city ||
                  touched.address.state ||
                  touched.address.zip)
              }
            >
              <StickyInput
                id="address.city"
                placeholder="City"
                values={values}
              />

              <StickySelect
                error={errors.address && errors.address.state}
                id="address.state"
                className="my-4"
                options={states}
                placeholder="State"
                values={values}
              />

              <StickyInput
                error={errors.address && errors.address.zip}
                id="address.zip"
                className="my-2"
                mask="99999"
                placeholder="Zip"
                values={values}
              />
            </ColumnInput>

            <div className="flex justify-evenly mt-4 mb-3">
              <CTAButton
                buttonClass="w-1/2 mx-auto md:w-full bg-primary-blue font-avenir-medium tracking-wider text-white rounded-full py-2 px-16 text-sm md:text-base inline-block hover:bg-navy-blue flex justify-center items-center"
                buttonText="Save"
                disabled={isSubmitting || isLoading}
                isLoading={isSubmitting || isLoading}
                icon={['fal', 'lock-alt']}
              />
              <CTAButton
                buttonClass="w-1/2 mx-auto md:w-full bg-red-300 font-avenir-medium tracking-wider text-negative-red rounded-full py-2 px-16 text-sm md:text-base inline-block hover:bg-negative-red hover:text-white flex justify-center items-center"
                buttonText="Cancel"
                disabled={isSubmitting || isLoading}
                isLoading={isSubmitting || isLoading}
                onClick={toggleAddOwner}
                icon={['fal', 'times']}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
